import React, { useEffect, useState } from "react";
import { Newsletter } from "@/lib/api";
import { useNewsletterModal } from "@/lib/modal";
import { ArrowUpRight } from "lucide-react";

function fmtDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d
        .toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
        })
        .toUpperCase();
}

export default function NewsletterSection() {
    const [posts, setPosts] = useState([]);
    const { openNewsletter } = useNewsletterModal();

    useEffect(() => {
        Newsletter.posts().then(setPosts).catch(() => {});
    }, []);

    return (
        <section id="newsletter" className="relative z-10" data-testid="newsletter-section">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-16 md:pt-24">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <div className="overline-cobalt mb-3">§ Newsletter</div>
                        <h2
                            className="font-display"
                            style={{
                                fontSize: "clamp(36px, 5vw, 64px)",
                                lineHeight: 0.98,
                                color: "var(--ink)",
                            }}
                        >
                            Weekly{" "}
                            <span
                                style={{
                                    fontFamily: "'Fraunces', serif",
                                    fontStyle: "italic",
                                    fontWeight: 400,
                                    color: "var(--cobalt)",
                                }}
                            >
                                intelligence
                            </span>{" "}
                            from the frontier.
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={openNewsletter}
                        className="btn-primary hidden md:inline-flex"
                        data-testid="newsletter-section-cta"
                    >
                        Join The Newsletter →
                    </button>
                </div>

                {posts.length === 0 ? (
                    <div className="panel p-12 md:p-16 text-center" data-testid="newsletter-empty">
                        <div className="overline-cobalt mb-4">[Status · Pending]</div>
                        <h3
                            className="font-display"
                            style={{
                                fontSize: "clamp(28px, 3.4vw, 44px)",
                                lineHeight: 1.05,
                                color: "var(--ink)",
                            }}
                        >
                            Coming{" "}
                            <span
                                style={{
                                    fontFamily: "'Fraunces', serif",
                                    fontStyle: "italic",
                                    fontWeight: 400,
                                    color: "var(--cobalt)",
                                    marginLeft: "0.15em",
                                }}
                            >
                                soon.
                            </span>
                        </h3>
                        <p
                            className="mt-4 max-w-md mx-auto"
                            style={{
                                color: "var(--ink-2)",
                                fontSize: 14,
                                lineHeight: 1.65,
                            }}
                        >
                            Weekly intelligence on the companies, capital, and
                            technical bets shaping the next decade. Subscribe
                            to get each dispatch first.
                        </p>
                    </div>
                ) : (
                    <ul className="panel" data-testid="newsletter-posts">
                        {posts.map((p) => (
                            <li
                                key={p.id}
                                className="group grid grid-cols-12 gap-3 items-baseline px-5 py-5 border-b last:border-b-0 transition-colors hover:bg-[var(--panel-2)]"
                                style={{ borderColor: "var(--rule-soft)" }}
                            >
                                <div className="col-span-3 md:col-span-2 overline" style={{ color: "var(--cobalt)" }}>
                                    {fmtDate(p.published_at)}
                                </div>
                                <div
                                    className="col-span-2 md:col-span-1 font-mono-tech"
                                    style={{ fontSize: 11, color: "var(--muted)", letterSpacing: "0.08em" }}
                                >
                                    {p.issue_number ? `№ ${String(p.issue_number).padStart(3, "0")}` : "—"}
                                </div>
                                <div className="col-span-7 md:col-span-8">
                                    <a href={p.url || "#"} target={p.url && p.url !== "#" ? "_blank" : undefined} rel="noreferrer" className="block">
                                        <h3
                                            style={{
                                                fontFamily: "'Inter', sans-serif",
                                                fontWeight: 700,
                                                fontSize: 18,
                                                color: "var(--ink)",
                                                letterSpacing: "-0.01em",
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {p.title}
                                        </h3>
                                        <p style={{ color: "var(--ink-2)", fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>
                                            {p.excerpt}
                                        </p>
                                    </a>
                                </div>
                                <div className="col-span-12 md:col-span-1 text-right opacity-0 group-hover:opacity-100 transition-opacity">
                                    <ArrowUpRight size={16} style={{ color: "var(--cobalt)" }} />
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="mt-6 md:hidden">
                    <button type="button" onClick={openNewsletter} className="btn-primary w-full justify-center">
                        Join The Newsletter →
                    </button>
                </div>
            </div>
        </section>
    );
}
